export interface CompressionOptions {
    maxWidthOrHeight: number;
    quality: number; // 0 to 1
}

/**
 * Compresses an image file using the browser's Canvas API.
 * Returns the base64 string (without the data URL prefix) and the mime type.
 */
export const compressImage = (
    file: File,
    options: CompressionOptions = { maxWidthOrHeight: 1024, quality: 0.8 }
): Promise<{ base64: string; mimeType: string }> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
            const img = new Image();
            img.src = event.target?.result as string;
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > height) {
                    if (width > options.maxWidthOrHeight) {
                        height = Math.round((height * options.maxWidthOrHeight) / width);
                        width = options.maxWidthOrHeight;
                    }
                } else {
                    if (height > options.maxWidthOrHeight) {
                        width = Math.round((width * options.maxWidthOrHeight) / height);
                        height = options.maxWidthOrHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');

                if (!ctx) {
                    reject(new Error('Failed to get canvas context'));
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);

                // Quality Reduction Loop to enforce 1MB limit
                let quality = options.quality;
                let mimeType = 'image/jpeg';
                let dataUrl = canvas.toDataURL(mimeType, quality);

                // Reduce quality until under 1MB (approx 1.33MB base64 length)
                while (dataUrl.length > 1330000 && quality > 0.1) {
                    quality -= 0.1;
                    dataUrl = canvas.toDataURL(mimeType, quality);
                }

                const base64 = dataUrl.split(',')[1];
                resolve({ base64, mimeType });
            };
            img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
    });
};
